import { useEffect, useState } from "react";
import { ArrowUp } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

/**
 * Combined scroll-progress bar (top) + floating scroll-to-top button (bottom-right).
 * Visible only after the user scrolls past a small threshold.
 */
export default function ScrollToTop() {
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const max = (h.scrollHeight - h.clientHeight) || 1;
      const p = Math.min(1, Math.max(0, h.scrollTop / max));
      setProgress(p);
      setVisible(h.scrollTop > 320);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      {/* Scroll progress bar */}
      <div
        aria-hidden
        data-testid="scroll-progress"
        className="fixed top-0 left-0 right-0 z-[100] h-[2px] bg-transparent pointer-events-none"
      >
        <div
          className="h-full bg-stenzia-green origin-left transition-transform duration-150 ease-out"
          style={{ transform: `scaleX(${progress})` }}
        />
      </div>

      {/* Scroll to top button */}
      <AnimatePresence>
        {visible && (
          <motion.button
            type="button"
            initial={{ opacity: 0, y: 16, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.9 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            data-testid="scroll-to-top"
            aria-label="Scroll to top"
            className="fixed bottom-24 right-6 z-50 w-12 h-12 rounded-full bg-charcoal text-white shadow-2xl border border-white/10 backdrop-blur-md flex items-center justify-center hover:bg-stenzia-green hover:scale-110 transition-all"
          >
            <ArrowUp size={20} />
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
}
